import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Upload,
  X,
  Waves,
  Sparkles,
  Bath,
  Sun,
  Settings2,
  Smartphone,
  LayoutGrid,
  Wrench,
  HelpCircle,
  Droplets,
  Phone,
  Check,
} from "lucide-react";
import api from "@/lib/api";

const STEPS = [
  { id: 1, label: "Service" },
  { id: 2, label: "Pool" },
  { id: 3, label: "Upgrades" },
  { id: 4, label: "Photos" },
  { id: 5, label: "Timeline" },
  { id: 6, label: "Contact" },
];

const SERVICES = [
  { value: "Complete Remodel", icon: Waves },
  { value: "Resurface Only", icon: Sparkles },
  { value: "Spa Addition", icon: Bath },
  { value: "Sunshelf", icon: Sun },
  { value: "Equipment Upgrade", icon: Settings2 },
  { value: "Pool Automation", icon: Smartphone },
  { value: "Tile & Coping", icon: LayoutGrid },
  { value: "New Plumbing", icon: Wrench },
  { value: "Water Features", icon: Droplets },
  { value: "Not Sure", icon: HelpCircle },
];

const POOL_AGE = ["0–5 yrs", "6–15 yrs", "15+ yrs", "Not sure"];
const POOL_SIZE = ["Small (under 300 sq ft)", "Medium (300–500 sq ft)", "Large (500+ sq ft)", "Not sure"];
const FINISHES = ["Plaster", "Pebble", "Quartz", "Tile", "Not sure"];
const REASONS = [
  "Cracks or surface damage",
  "Staining / discoloration",
  "Aesthetic refresh",
  "Leaks",
  "Old / failing equipment",
  "Full luxury upgrade",
];
const UPGRADES = [
  "LED Lighting",
  "Salt System",
  "Heater",
  "Automation",
  "Waterfall",
  "Bubblers",
  "Deck Jets",
  "Laminar Jets",
  "New Tile",
  "Travertine Coping",
  "Sunshelf",
  "Spillover Spa",
  "Energy-Efficient Pump",
];
const TIMELINES = ["ASAP", "1–3 months", "3–6 months", "Just exploring"];
const BUDGETS = ["$10k – $25k", "$25k – $50k", "$50k+", "Not sure yet"];

const STEP_VARIANTS = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

export default function QuoteWizard() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]); // {file, preview}
  const [data, setData] = useState({
    service_interest: "",
    pool_age: "",
    pool_size: "",
    current_finish: "",
    project_reasons: [],
    desired_upgrades: [],
    timeline: "",
    budget: "",
    notes: "",
    name: "",
    phone: "",
    email: "",
    city: "",
    address: "",
    website: "", // spam honeypot
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleArr = (k, v) =>
    setData((d) => {
      const arr = d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v];
      return { ...d, [k]: arr };
    });

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length));
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  const onPickFiles = (filesList) => {
    const files = Array.from(filesList || []);
    const remaining = 4 - photos.length;
    const selected = files.slice(0, remaining);
    const newPhotos = selected.map((f) => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name,
    }));
    setPhotos((p) => [...p, ...newPhotos]);
  };

  const removePhoto = (i) =>
    setPhotos((p) => {
      URL.revokeObjectURL(p[i].preview);
      return p.filter((_, idx) => idx !== i);
    });

  const canNext = () => {
    if (step === 1) return !!data.service_interest;
    if (step === 6) return data.name && data.phone && data.email && data.city;
    return true;
  };

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", data.name);
      fd.append("phone", data.phone);
      fd.append("email", data.email);
      fd.append("city", data.city);
      fd.append("address", data.address);
      fd.append("service_interest", data.service_interest);
      fd.append("pool_age", data.pool_age);
      fd.append("pool_size", data.pool_size);
      fd.append("current_finish", data.current_finish);
      fd.append("project_reasons", JSON.stringify(data.project_reasons));
      fd.append("desired_upgrades", JSON.stringify(data.desired_upgrades));
      fd.append("timeline", data.timeline);
      fd.append("budget", data.budget);
      fd.append("notes", data.notes);
      fd.append("website", data.website || "");
      photos.forEach((p) => fd.append("photos", p.file, p.name));

      await api.post("/quote-requests", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
    } catch (err) {
      toast.error("Submission failed", {
        description: err?.response?.data?.detail || "Please try again or call 954-328-8083.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white grid place-items-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-xl w-full text-center"
          data-testid="quote-success"
        >
          <div className="w-20 h-20 mx-auto rounded-full bg-[#00B4D8]/10 grid place-items-center mb-6">
            <CheckCircle2 className="w-10 h-10 text-[#00B4D8]" strokeWidth={1.8} />
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-light text-slate-900 tracking-tight leading-[1.1]">
            Thanks, <span className="italic font-medium text-[#0F3A5A]">{data.name.split(" ")[0] || "there"}</span>.
            <br />
            We've got it.
          </h1>
          <p className="text-slate-600 mt-6 leading-relaxed text-lg">
            A pool specialist will review your project and call you within{" "}
            <strong className="text-[#0F3A5A]">24 to 48 hours</strong> to schedule a
            jobsite visit or send a custom estimate.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="tel:9543288083"
              className="inline-flex items-center justify-center gap-2 bg-[#00B4D8] hover:bg-[#0096B4] text-white px-7 py-3.5 rounded-full font-semibold shadow-lg transition"
              data-testid="success-call-btn"
            >
              <Phone className="w-4 h-4" />
              Need to talk now? 954-328-8083
            </a>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-400 text-slate-700 px-7 py-3.5 rounded-full font-semibold transition"
              data-testid="success-home-btn"
            >
              Back to Homepage
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  const progress = (step / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="quote-logo">
            <span className="grid place-items-center w-9 h-9 rounded-lg bg-[#0F3A5A] text-white font-display font-bold text-sm">
              AZ
            </span>
            <span className="font-display font-semibold text-lg text-[#0F3A5A]">AZ Pools</span>
          </Link>
          <a
            href="tel:9543288083"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#0F3A5A] hover:text-[#00B4D8]"
            data-testid="quote-header-call"
          >
            <Phone className="w-4 h-4" />
            954-328-8083
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-10 md:py-14">
        {/* Heading */}
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] font-semibold text-[#00B4D8] mb-3">
            Instant Quote Request
          </p>
          <h1 className="font-display text-3xl md:text-5xl font-light text-slate-900 tracking-tight leading-[1.05]">
            Tell us about your project.
          </h1>
          <p className="text-slate-500 mt-3">
            Takes about 90 seconds — no pricing surprises, just a real conversation.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-10" data-testid="quote-progress">
          <div className="flex justify-between text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 mb-2">
            <span>Step {step} of {STEPS.length}</span>
            <span>{STEPS[step - 1].label}</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-[#00B4D8]"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* Card */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-10 min-h-[420px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              variants={STEP_VARIANTS}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 1 && (
                <Step
                  title="What service are you interested in?"
                  subtitle="Pick the option closest to your project — we'll refine the details together."
                >
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="step-service">
                    {SERVICES.map((s, i) => {
                      const Icon = s.icon;
                      const active = data.service_interest === s.value;
                      // Center the lone last item when total % 3 === 1 (e.g. 10 items)
                      const isLone =
                        i === SERVICES.length - 1 && SERVICES.length % 3 === 1;
                      return (
                        <button
                          key={s.value}
                          type="button"
                          onClick={() => set("service_interest", s.value)}
                          data-testid={`service-tile-${s.value.replace(/\s+/g, "-").toLowerCase()}`}
                          className={`group p-5 rounded-2xl border text-left transition-all ${
                            isLone ? "md:col-start-2" : ""
                          } ${
                            active
                              ? "border-[#0F3A5A] bg-[#0F3A5A] text-white shadow-lg"
                              : "border-slate-200 hover:border-[#00B4D8] hover:-translate-y-0.5"
                          }`}
                        >
                          <Icon
                            className={`w-5 h-5 mb-3 ${active ? "text-[#A8E6F0]" : "text-[#0F3A5A]"}`}
                            strokeWidth={1.7}
                          />
                          <p className="font-display font-semibold text-sm tracking-tight">
                            {s.value}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </Step>
              )}

              {step === 2 && (
                <Step
                  title="Tell us about your pool."
                  subtitle="A few quick details so we come prepared."
                >
                  <div className="space-y-7" data-testid="step-pool">
                    <ChipGroup label="Pool age" options={POOL_AGE} value={data.pool_age} onChange={(v) => set("pool_age", v)} testidPrefix="age" />
                    <ChipGroup label="Approximate size" options={POOL_SIZE} value={data.pool_size} onChange={(v) => set("pool_size", v)} testidPrefix="size" />
                    <ChipGroup label="Current finish" options={FINISHES} value={data.current_finish} onChange={(v) => set("current_finish", v)} testidPrefix="finish" />
                    <MultiChipGroup
                      label="What's prompting the project?"
                      options={REASONS}
                      values={data.project_reasons}
                      onToggle={(v) => toggleArr("project_reasons", v)}
                      testidPrefix="reason"
                    />
                  </div>
                </Step>
              )}

              {step === 3 && (
                <Step
                  title="Any upgrades you're considering?"
                  subtitle="Multi-select — pick everything that sounds interesting. Skip if unsure."
                >
                  <div data-testid="step-upgrades">
                    <MultiChipGroup
                      label=""
                      options={UPGRADES}
                      values={data.desired_upgrades}
                      onToggle={(v) => toggleArr("desired_upgrades", v)}
                      testidPrefix="upgrade"
                    />
                  </div>
                </Step>
              )}

              {step === 4 && (
                <Step
                  title="Photos of your current pool (optional)"
                  subtitle="Up to 4 photos — helps us quote accurately without a first visit."
                >
                  <div data-testid="step-photos">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      onChange={(e) => onPickFiles(e.target.files)}
                      data-testid="photo-input"
                    />
                    {photos.length < 4 && (
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        data-testid="photo-upload-btn"
                        className="w-full border-2 border-dashed border-slate-300 hover:border-[#00B4D8] rounded-2xl p-10 text-center transition group"
                      >
                        <div className="w-12 h-12 mx-auto rounded-full bg-slate-100 group-hover:bg-[#00B4D8]/10 grid place-items-center mb-3">
                          <Upload className="w-5 h-5 text-slate-500 group-hover:text-[#00B4D8]" />
                        </div>
                        <p className="font-display font-medium text-slate-900">
                          Tap to upload photos
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          JPG / PNG — up to 4 photos, 3MB each
                        </p>
                      </button>
                    )}

                    {photos.length > 0 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="photo-previews">
                        {photos.map((p, i) => (
                          <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 group">
                            <img src={p.preview} alt={p.name} className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removePhoto(i)}
                              aria-label="Remove photo"
                              data-testid={`photo-remove-${i}`}
                              className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 hover:bg-black text-white grid place-items-center"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Step>
              )}

              {step === 5 && (
                <Step
                  title="When are you hoping to start?"
                  subtitle="And what budget range fits — optional but helps us prioritize."
                >
                  <div className="space-y-7" data-testid="step-timeline">
                    <ChipGroup label="Timeline" options={TIMELINES} value={data.timeline} onChange={(v) => set("timeline", v)} testidPrefix="timeline" />
                    <ChipGroup label="Budget range" options={BUDGETS} value={data.budget} onChange={(v) => set("budget", v)} testidPrefix="budget" />
                    <div>
                      <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                        Anything else? (optional)
                      </label>
                      <textarea
                        value={data.notes}
                        onChange={(e) => set("notes", e.target.value)}
                        rows={3}
                        placeholder="Tell us anything that would help us prepare for our call…"
                        data-testid="notes-input"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 resize-none"
                      />
                    </div>
                  </div>
                </Step>
              )}

              {step === 6 && (
                <Step
                  title="Where should we send your custom estimate?"
                  subtitle="A specialist will call within 24 to 48 hours to schedule a jobsite visit or send a tailored estimate."
                >
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="step-contact">
                    <Field label="Full Name" required value={data.name} onChange={(v) => set("name", v)} testid="contact-name" />
                    <Field label="Phone" required type="tel" value={data.phone} onChange={(v) => set("phone", v)} testid="contact-phone" />
                    <Field label="Email" required type="email" value={data.email} onChange={(v) => set("email", v)} testid="contact-email" className="sm:col-span-2" />
                    <Field label="City" required value={data.city} onChange={(v) => set("city", v)} testid="contact-city" />
                    <Field label="Property Address (optional)" value={data.address} onChange={(v) => set("address", v)} testid="contact-address" />
                    {/* Honeypot — invisible to humans, bots fill it and get silently dropped */}
                    <div
                      aria-hidden="true"
                      style={{
                        position: "absolute",
                        left: "-9999px",
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                      }}
                    >
                      <label htmlFor="quote-website-hp">Leave this field blank</label>
                      <input
                        id="quote-website-hp"
                        type="text"
                        name="website"
                        tabIndex={-1}
                        autoComplete="off"
                        value={data.website || ""}
                        onChange={(e) => set("website", e.target.value)}
                      />
                    </div>
                  </div>
                </Step>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Nav buttons */}
        <div className="flex items-center justify-between mt-7">
          <button
            type="button"
            onClick={prev}
            disabled={step === 1}
            data-testid="quote-back-btn"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-semibold px-4 py-2 rounded-full"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          {step < STEPS.length ? (
            <button
              type="button"
              onClick={next}
              disabled={!canNext()}
              data-testid="quote-next-btn"
              className="inline-flex items-center gap-2 bg-[#0F3A5A] hover:bg-[#174E7A] disabled:opacity-40 disabled:cursor-not-allowed text-white px-7 py-3.5 rounded-full font-semibold shadow-lg transition"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canNext() || submitting}
              data-testid="quote-submit-btn"
              className="inline-flex items-center gap-2 bg-[#00B4D8] hover:bg-[#0096B4] disabled:opacity-50 text-white px-7 py-3.5 rounded-full font-semibold shadow-lg transition"
            >
              {submitting ? "Sending…" : (<>Submit Quote Request <ArrowRight className="w-4 h-4" /></>)}
            </button>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-8">
          By submitting, you agree to be contacted about your project. We never share your info.
        </p>
      </div>
    </div>
  );
}

function Step({ title, subtitle, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
        {title}
      </h2>
      {subtitle && <p className="text-slate-500 mt-2 mb-7 leading-relaxed">{subtitle}</p>}
      {children}
    </div>
  );
}

function ChipGroup({ label, options, value, onChange, testidPrefix }) {
  return (
    <div>
      {label && (
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">{label}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = value === o;
          return (
            <button
              key={o}
              type="button"
              onClick={() => onChange(o)}
              data-testid={`${testidPrefix}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className={`px-4 py-2.5 rounded-full text-sm font-medium border transition ${
                active
                  ? "bg-[#0F3A5A] border-[#0F3A5A] text-white shadow"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiChipGroup({ label, options, values, onToggle, testidPrefix }) {
  return (
    <div>
      {label && (
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">{label}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = values.includes(o);
          return (
            <button
              key={o}
              type="button"
              onClick={() => onToggle(o)}
              data-testid={`${testidPrefix}-${o.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium border transition ${
                active
                  ? "bg-[#00B4D8] border-[#00B4D8] text-white shadow"
                  : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"
              }`}
            >
              {active && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
              {o}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, required, type = "text", value, onChange, testid, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
        {label} {required && <span className="text-rose-500">*</span>}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#00B4D8] focus:ring-2 focus:ring-[#00B4D8]/20 transition"
      />
    </div>
  );
}
